import React, { useEffect, useMemo, useState } from "react";

import AdminLogoHeader from "../../components/admin/AdminLogoHeader";
import LoadingSpinner from "../../components/LoadingSpinner";
import api from "../../services/api";

const emptyForm = {
  name: "",
  account_class: "asset",
  currency: "NGN",
  parent_id: "",
};

const classLabels = {
  asset: "Asset",
  liability: "Liability",
  equity: "Equity",
  income: "Income",
  expense: "Expense",
};

const ChartOfAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [classFilter, setClassFilter] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const accountsList = await api.adminListChartOfAccounts({ include_inactive: true });
      setAccounts(Array.isArray(accountsList) ? accountsList : []);
    } catch (err) {
      setError(err.message || "Unable to load chart of accounts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredAccounts = useMemo(() => {
    if (!classFilter) return accounts;
    return accounts.filter((acct) => acct.account_class === classFilter);
  }, [accounts, classFilter]);

  const accountTreeByClass = useMemo(() => {
    const grouped = {};
    const data = filteredAccounts;
    data.forEach((acct) => {
      const cls = acct.account_class || "uncategorized";
      if (!grouped[cls]) grouped[cls] = [];
    });

    const byId = Object.fromEntries(data.map((a) => [a.id, a]));
    const children = {};
    data.forEach((a) => {
      const pid = a.parent_id || null;
      children[pid] = children[pid] || [];
      children[pid].push(a);
    });

    const build = (pid = null, depth = 0, acc = []) => {
      const list = (children[pid] || []).sort((a, b) => (a.code || "").localeCompare(b.code || ""));
      list.forEach((node) => {
        acc.push({ ...node, depth });
        build(node.id, depth + 1, acc);
      });
      return acc;
    };

    Object.keys(grouped).forEach((cls) => {
      grouped[cls] = build(null, 0, []).filter((a) => (a.account_class || "") === cls);
    });
    return grouped;
  }, [filteredAccounts]);

  const parentOptions = useMemo(
    () =>
      accounts.filter(
        (acct) =>
          acct.account_class === form.account_class &&
          acct.is_active
      ),
    [accounts, form.account_class]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "account_class" ? value : value,
      ...(name === "account_class" ? { parent_id: "" } : {}),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");
    const payload = {
      name: form.name.trim(),
      account_class: form.account_class,
      currency: (form.currency || "NGN").trim().toUpperCase() || "NGN",
      parent_id: form.parent_id ? Number(form.parent_id) : null,
    };

    if (!payload.name) {
      setError("Account name is required.");
      setSaving(false);
      return;
    }

    try {
      const created = await api.adminCreateLedgerAccount(payload);
      setSuccess(`Created ${created.code} - ${created.name}.`);
      setForm(emptyForm);
      loadData();
    } catch (err) {
      setError(err.message || "Unable to create account.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (account) => {
    try {
      await api.adminUpdateLedgerAccount(account.id, { is_active: !account.is_active });
      loadData();
    } catch (err) {
      alert(err.message || "Unable to update status.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminLogoHeader />
      <div className="mx-auto max-w-5xl space-y-6 p-6">
        <section className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold text-slate-900">Chart of accounts</h1>
            <p className="text-sm text-slate-500">
              Create ledger accounts by category. Codes are auto-generated and become available everywhere accounts are selected.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            {success ? <p className="text-sm text-emerald-600">{success}</p> : null}

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Account name</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Category</label>
                <select
                  name="account_class"
                  value={form.account_class}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  {Object.keys(classLabels).map((key) => (
                    <option key={key} value={key}>
                      {classLabels[key]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-slate-700">Currency</label>
                <input
                  type="text"
                  name="currency"
                  value={form.currency}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Parent account (optional)</label>
                <select
                  name="parent_id"
                  value={form.parent_id}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="">No parent</option>
                  {parentOptions.map((acct) => (
                    <option key={acct.id} value={acct.id}>
                      {acct.code} - {acct.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Create account"}
              </button>
            </div>
          </form>
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
          <div className="border-b border-slate-100 px-6 py-4">
            <h2 className="text-lg font-semibold text-slate-900">Ledger accounts</h2>
            <p className="text-sm text-slate-500">
              All active and inactive accounts across assets, liabilities, equity, income, and expenses.
            </p>
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm"
              >
                <option value="">All categories</option>
                {Object.keys(classLabels).map((key) => (
                  <option key={key} value={key}>
                    {classLabels[key]}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setClassFilter("")}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
              >
                Reset
              </button>
            </div>
          </div>
          {loading ? (
            <div className="px-6 py-6">
              <LoadingSpinner />
            </div>
          ) : !accounts.length ? (
            <p className="px-6 py-6 text-sm text-slate-500">No accounts have been added yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Code</th>
                    <th className="px-4 py-3 text-left font-medium">Name</th>
                    <th className="px-4 py-3 text-left font-medium">Class</th>
                    <th className="px-4 py-3 text-left font-medium">Currency</th>
                    <th className="px-4 py-3 text-left font-medium">Parent</th>
                    <th className="px-4 py-3 text-left font-medium">Status</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {Object.keys(accountTreeByClass)
                    .sort()
                    .map((cls) => (
                      <React.Fragment key={cls}>
                        <tr className="bg-slate-50/60">
                          <td className="px-4 py-2 font-semibold text-slate-800" colSpan={7}>
                            {classLabels[cls] || cls}
                          </td>
                        </tr>
                        {accountTreeByClass[cls].map((account) => (
                          <tr key={account.id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-slate-700 font-medium">{account.code}</td>
                            <td className="px-4 py-3 text-slate-700">
                              <span style={{ paddingLeft: `${account.depth * 12}px` }}>
                                {account.name}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600 capitalize">
                              {classLabels[account.account_class] || account.account_class}
                            </td>
                            <td className="px-4 py-3 text-slate-600">{account.currency}</td>
                            <td className="px-4 py-3 text-slate-600">
                              {account.parent_id
                                ? (accounts.find((a) => a.id === account.parent_id)?.code || account.parent_id)
                                : "-"}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`rounded-full px-2 py-0.5 text-xs ${
                                  account.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
                                }`}
                              >
                                {account.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleToggleActive(account)}
                                  className={`rounded-lg px-3 py-1.5 text-xs ${
                                    account.is_active
                                      ? "border border-amber-200 text-amber-700 hover:bg-amber-50"
                                      : "border border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                  }`}
                                >
                                  {account.is_active ? "Disable" : "Activate"}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ChartOfAccounts;
